import React from 'react';

export function renderInline(text) {
  // Match **bold** first, then *italic* (rendered as bold for readability)
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(p))     return <strong key={i}>{p.slice(1, -1)}</strong>;
    return p;
  });
}

// ── Table rendering ──────────────────────────────────────────────────────────
const SEV_STYLE = {
  '🔴': { color: '#c0392b', background: 'rgba(192,57,43,0.10)' },
  '🟠': { color: '#d35400', background: 'rgba(211,84,0,0.10)' },
  '🟡': { color: '#b7950b', background: 'rgba(183,149,11,0.10)' },
  '🟢': { color: '#1e8449', background: 'rgba(30,132,73,0.10)'  },
};

function severityBadge(text) {
  for (const [emoji, style] of Object.entries(SEV_STYLE)) {
    if (text.includes(emoji)) {
      return (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
          color: style.color, background: style.background,
        }}>
          {emoji} {text.replace(emoji, '').trim()}
        </span>
      );
    }
  }
  return renderInline(text);
}

function renderTable(tableLines, key) {
  // Filter out pure separator lines (|---|---|)
  const nonSep = tableLines.filter(l => !/^\|[\s\-:|]+\|[\s\-:|]*$/.test(l.trim()));
  if (nonSep.length === 0) return null;

  const parseRow = (line) =>
    line.split('|').map(c => c.trim()).filter((_, i, a) => i > 0 && i < a.length - 1);

  const headers = parseRow(nonSep[0]);
  const dataRows = nonSep.slice(1);

  // Detect which column index is "Severity" for special rendering
  const sevIdx = headers.findIndex(h => /severity/i.test(h));

  return (
    <div key={key} style={{ overflowX: 'auto', margin: '12px 0' }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: 13,
        border: '1px solid #dce3ec', borderRadius: 6, overflow: 'hidden',
      }}>
        <thead>
          <tr style={{ background: '#eef2f8' }}>
            {headers.map((h, i) => (
              <th key={i} style={{
                padding: '8px 12px', textAlign: 'left', fontWeight: 700,
                color: '#3a4f6e', fontSize: 11, letterSpacing: '0.06em',
                borderBottom: '1px solid #dce3ec', whiteSpace: 'nowrap',
              }}>
                {h.toUpperCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, ri) => {
            const cells = parseRow(row);
            return (
              <tr key={ri} style={{ background: ri % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                {cells.map((c, ci) => (
                  <td key={ci} style={{
                    padding: '7px 12px',
                    borderBottom: ri < dataRows.length - 1 ? '1px solid #eaeff5' : 'none',
                    color: ci === 0 ? '#8892a0' : '#2c3e50',
                    whiteSpace: ci === 0 || ci === cells.length - 1 ? 'nowrap' : undefined,
                  }}>
                    {ci === sevIdx ? severityBadge(c) : renderInline(c)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Main renderer ────────────────────────────────────────────────────────────
export function renderMarkdown(text) {
  let qNum = 0;
  const lines = text.split('\n');
  const elements = [];
  let tableAccum = [];   // buffer for consecutive | lines
  let listAccum  = [];   // buffer for consecutive list items
  let lineKey = 0;

  const flushTable = () => {
    if (tableAccum.length === 0) return;
    const el = renderTable(tableAccum, `tbl-${lineKey}`);
    if (el) elements.push(el);
    tableAccum = [];
  };

  const flushList = () => {
    if (listAccum.length === 0) return;
    elements.push(
      <ul key={`ul-${lineKey}`} style={{ margin: '4px 0 8px 18px', padding: 0, listStyleType: 'disc' }}>
        {listAccum}
      </ul>
    );
    listAccum = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    lineKey = i;

    // ── Accumulate table rows ──
    if (line.trim().startsWith('|')) {
      flushList();
      tableAccum.push(line);
      continue;
    }
    // Non-table line → flush any pending table first
    flushTable();

    // ── Headings → bold paragraph (not h-tags) ──
    if (/^###\s/.test(line)) {
      flushList();
      elements.push(
        <p key={i} style={{ margin: '10px 0 3px', fontWeight: 700, fontSize: 12.5, color: '#2c3e50' }}>
          {renderInline(line.replace(/^###\s/, ''))}
        </p>
      );
      continue;
    }
    if (/^##\s/.test(line)) {
      flushList();
      elements.push(
        <p key={i} style={{ margin: '12px 0 4px', fontWeight: 700, fontSize: 13, color: '#2c3e50' }}>
          {renderInline(line.replace(/^##\s/, ''))}
        </p>
      );
      continue;
    }
    if (/^#\s/.test(line)) {
      flushList();
      elements.push(
        <p key={i} style={{ margin: '14px 0 4px', fontWeight: 700, fontSize: 14, color: '#2c3e50' }}>
          {renderInline(line.replace(/^#\s/, ''))}
        </p>
      );
      continue;
    }

    // ── Questions ──
    const isQuestion = /^QUESTION: /.test(line) || /^\? ./.test(line);
    if (isQuestion) {
      flushList();
      qNum++;
      const question = line.replace(/^QUESTION: /, '').replace(/^\? /, '');
      if (qNum === 1) {
        elements.push(
          <p key={`q-intro-${i}`} style={{ margin: '14px 0 6px', color: '#6b7a8d', fontSize: 12, fontStyle: 'italic' }}>
            Please answer the questions below to help Claude understand the context better:
          </p>
        );
      }
      elements.push(
        <p key={i} style={{ margin: '4px 0', lineHeight: 1.6, fontWeight: 700, display: 'flex', gap: 7 }}>
          <span style={{ color: '#3E68B2', minWidth: 18, flexShrink: 0 }}>{qNum}.</span>
          <span>{renderInline(question)}</span>
        </p>
      );
      continue;
    }

    // ── Lone '?' leftover ──
    if (line.trim() === '?') continue;

    // ── List items → accumulate for grouped <ul> ──
    if (/^\d+\.\s/.test(line) || /^[-*]\s/.test(line)) {
      const content = line.replace(/^\d+\.\s+/, '').replace(/^[-*]\s+/, '');
      listAccum.push(
        <li key={i} style={{ marginBottom: 3, lineHeight: 1.55, color: '#2c3e50' }}>
          {renderInline(content)}
        </li>
      );
      continue;
    }
    // Non-list line → flush any pending list
    flushList();

    // ── Blank lines ──
    if (line.trim() === '') { elements.push(<div key={i} style={{ height: 6 }} />); continue; }

    // ── Plain paragraph ──
    elements.push(<p key={i} style={{ margin: '4px 0', lineHeight: 1.6 }}>{renderInline(line)}</p>);
  }

  // Flush any trailing buffers
  flushTable();
  flushList();

  return elements.filter(Boolean);
}
